import { Link } from 'react-router-dom';
import { LucideIcon, ArrowRight, ArrowLeft } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimation';
import SEO from './SEO';

interface Outcome {
  title: string;
  description: string;
}

interface SolutionPageProps {
  label: string;
  headline: string;
  subheadline: string;
  heroImage: string;
  problemTitle: string;
  problemBody: string[];
  outcomes: Outcome[];
  icon: LucideIcon;
  whatWeDoTitle: string;
  whatWeDoBody: string[];
  whatYouGain: string[];
  relatedSolutions: { title: string; href: string }[];
}

export default function SolutionPage({
  label,
  headline,
  subheadline,
  heroImage,
  problemTitle,
  problemBody,
  outcomes,
  icon: Icon,
  whatWeDoTitle,
  whatWeDoBody,
  whatYouGain,
  relatedSolutions,
}: SolutionPageProps) {
  const containerRef = useScrollAnimations();

  return (
    <div ref={containerRef}>
      <SEO 
        title={`${headline} | 7th Vision Solutions`}
        description={subheadline}
      />
      {/* Hero */}
      <section className="relative min-h-[55vh] sm:min-h-[70vh] bg-stone-900 flex flex-col justify-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/30 to-stone-900" />
        <div className="relative section-padding pb-12 sm:pb-20 pt-28 sm:pt-40">
          <Link
            to="/#solutions"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-stone-500 hover:text-stone-300 transition-colors duration-300 mb-10"
          >
            <ArrowLeft size={12} />
            All Solutions
          </Link>
          <p className="section-label text-bronze-400 mb-4">{label}</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-100 leading-tight max-w-3xl mb-6">
            {headline}
          </h1>
          <p className="text-stone-400 text-lg font-light max-w-xl leading-relaxed">
            {subheadline}
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="section-padding py-14 md:py-28 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20">
            <div className="fade-up">
              <p className="section-label mb-4">The Operational Challenge</p>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-800 leading-tight mb-8">
                {problemTitle}
              </h2>
              <div className="w-12 h-px bg-bronze-400 mb-8" />
              <div className="space-y-4">
                {problemBody.map((para, i) => (
                  <p key={i} className="text-stone-600 leading-relaxed font-light">
                    {para}
                  </p>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center fade-in">
              <div className="w-32 h-32 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center">
                <Icon size={40} className="text-bronze-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Improves */}
      <section className="section-padding py-14 md:py-28 bg-stone-800">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 md:mb-16 fade-up">
            <p className="section-label text-bronze-400 mb-4">What Improves</p>
            <h2 className="font-serif text-3xl md:text-4xl text-stone-100 leading-tight max-w-2xl">
              The specific outcomes a well-built system creates.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-700">
            {outcomes.map((o, i) => (
              <div
                key={i}
                className="bg-stone-800 p-6 md:p-10 fade-up"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-8 h-px bg-bronze-500 mb-5" />
                <h3 className="font-serif text-xl text-stone-200 mb-3">{o.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{o.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section-padding py-14 md:py-28 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-start">
            <div className="fade-up">
              <p className="section-label mb-4">Our Approach</p>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-800 leading-tight mb-8">
                {whatWeDoTitle}
              </h2>
              <div className="w-12 h-px bg-bronze-400 mb-8" />
              <div className="space-y-4">
                {whatWeDoBody.map((para, i) => (
                  <p key={i} className="text-stone-600 leading-relaxed font-light">
                    {para}
                  </p>
                ))}
              </div>
            </div>

            <div className="fade-up" style={{ transitionDelay: '150ms' }}>
              <p className="section-label mb-8">What You Gain</p>
              <ul className="space-y-4">
                {whatYouGain.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-1 h-1 rounded-full bg-bronze-500 mt-2.5 flex-shrink-0" />
                    <p className="text-stone-600 leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="section-padding py-10 md:py-20 bg-stone-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-6 md:mb-12 fade-up">
            <h2 className="font-serif text-2xl text-stone-800">Other areas we can help with</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 fade-up">
            {relatedSolutions.map((s) => (
              <Link
                key={s.href}
                to={s.href}
                className="group flex items-center justify-between bg-white p-6 border border-stone-200 hover:border-stone-800 transition-colors duration-300"
              >
                <span className="font-serif text-stone-700 group-hover:text-stone-900 transition-colors duration-300">
                  {s.title}
                </span>
                <ArrowRight
                  size={14}
                  className="text-stone-400 group-hover:text-stone-800 group-hover:translate-x-1 transition-all duration-300"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-14 md:py-28 bg-stone-900">
        <div className="max-w-2xl mx-auto text-center fade-up">
          <h2 className="font-serif text-4xl text-stone-100 leading-tight mb-8">
            Interested in addressing this in your business?
          </h2>
          <div className="w-12 h-px bg-bronze-500 mx-auto mb-10" />
          <p className="text-stone-400 leading-relaxed mb-12 font-light">
            We start with a conversation. No pressure, no proposals before we understand
            what you actually need.
          </p>
          <Link to="/contact" className="btn-outline-light">
            Schedule a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
}
